/**
 * Recovery from a deploy landing while a tab is open.
 *
 * The shell a tab loaded names chunk hashes that a later deploy replaces. The old
 * hashes are gone from the CDN, so the next lazy route 404s and the app shows an
 * error screen for a page that is perfectly fine on a reload. Reloading picks up
 * the current index.html and its current hashes.
 *
 * THE BUG THIS REPLACES. There were two guards with two different policies:
 *
 *   App.tsx    sessionStorage["chunk-reload:<Route>"] = "1", never cleared.
 *   main.tsx   sessionStorage["bht_preload_reloaded"] = "1", cleared after 10s.
 *
 * The first is one reload per ROUTE PER TAB, for the life of the tab, and
 * sessionStorage survives reloads. On 2026-09-09 a signed-in student hit
 * "route chunk for PracticeTestReport loaded without its export" nine times in
 * three minutes on their own score report: the first skew spent the one reload,
 * a second deploy minutes later found the flag already set, and from then on
 * every attempt went straight to the error boundary whose only affordance is
 * "Back home". The report screen was unreachable for that tab, permanently, and
 * that is the "something went wrong" a student described on a finished test.
 *
 * The second guard has the opposite failure: a 10-second timer is a race, so a
 * genuinely missing export can reload forever once the app survives ten seconds.
 *
 * THE RULE. One reload per BUILD. Keyed on the running build's id, so:
 *   - a new deploy always gets a fresh attempt, which is exactly when retrying
 *     is the correct thing to do, and
 *   - a reload loop is impossible, because looping would require the build id to
 *     change on every load.
 *
 * Storage failures (Safari private browsing, blocked site data) fall back to
 * "do not reload": a stuck screen with a Reload button beats a reload loop.
 */
import { buildId } from "./build-id.js";

const PREFIX = "bht-chunk-reload:";

/**
 * The fallback loop guard when storage is unavailable. It rides in the URL,
 * which survives the reload it is guarding, so a second failure on the same
 * build finds it already set. Ugly in the address bar, and that is a fair price
 * for a page that works on a phone with site data blocked.
 */
const RELOAD_PARAM = "bhtr";

/**
 * True when the caller should reload NOW to pick up the current chunk graph.
 * Records the attempt first, so a second failure on the same build does not
 * reload again.
 *
 * `scope` separates the route-level guard from the preload-event guard without
 * letting either burn the other's attempt.
 */
/**
 * Import a lazy chunk, retrying a transient network failure before concluding the
 * chunk is gone.
 *
 * A deploy is not the only way a dynamic import fails. On 2026-09-11 production
 * reported "route chunk for TraplineDrill loaded without its export" twice from
 * build CJm-McG3, while CJm-McG3 was the LIVE build and its TraplineDrill chunk
 * served a clean 200 with the export present. Nothing was stale; the fetch had
 * failed, which is ordinary on a phone. The once-per-build reload guard then made
 * it permanent, because the reload landed on the same build with the guard spent.
 *
 * Two retries with a short backoff, then give up and let the caller decide the
 * chunk is missing. Returns undefined rather than throwing so the caller keeps one
 * code path for "no module" and "no export".
 */
export async function loadWithRetry<M>(
  load: () => Promise<M>,
  attempts = 3,
  delayMs = 250,
  sleep: (ms: number) => Promise<unknown> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<M | undefined> {
  for (let i = 0; i < attempts; i++) {
    try {
      return await load();
    } catch {
      // An immediate retry tends to hit the same dead connection, so back off.
      // No wait after the final attempt.
      if (i < attempts - 1) await sleep(delayMs * (i + 1));
    }
  }
  return undefined;
}

export function claimChunkReload(scope: string): boolean {
  const key = `${PREFIX}${scope}`;
  const build = buildId();
  try {
    if (sessionStorage.getItem(key) === build) return false;
    sessionStorage.setItem(key, build);
    return true;
  } catch {
    // NO STORAGE, SO USE THE URL. Returning false here meant a device with site
    // data blocked never recovered at all: a real visitor on an iPhone, signed
    // out, reading the blog on 2026-10-01 got the error boundary instead of the
    // page, because iOS private browsing throws on sessionStorage.
    //
    // The URL survives a reload and cannot loop, which is the property
    // sessionStorage was being used for. One marker carrying this build's id:
    // if it is already there, this build has had its attempt.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get(RELOAD_PARAM) === build) return false;
      url.searchParams.set(RELOAD_PARAM, build);
      window.history.replaceState(null, "", url.toString());
      return true;
    } catch {
      // Neither storage nor history. A stuck screen with a Reload button beats a
      // reload loop, which is the original reasoning and still right here.
      return false;
    }
  }
}
